import { useEffect, useRef } from 'react';
import { resolveMarkupColor, type ITextStyle, type MarkupColor } from '../../../utils/markupTypes';
import styles from './DrawingLayer.module.css';
import { isImeComposing } from '@utils/ime';

interface ITextBoxEditorProps {
  /** The box being written, in % of the content rect. */
  box: { x: number; y: number; w: number };
  value: string;
  /** The content rect's pixel width — the basis for the preview font size. */
  contentWidthPx: number;
  color: MarkupColor;
  textStyle: ITextStyle;
  inputLabel: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * The floating textarea a text box is written in: a live preview of the stamp
 * (same position, width, wrapping, format and ink as the baked result). Enter
 * commits, Shift+Enter breaks the line, Escape cancels — and deliberately
 * NOTHING on blur: the dialog's focus management can steal focus right as the
 * input mounts, which would instantly close it before anything could be typed.
 */
export function TextBoxEditor({
  box, value, contentWidthPx, color, textStyle, inputLabel, onChange, onCommit, onCancel,
}: ITextBoxEditorProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // autoFocus can lose to the dialog's focus management right as the input
  // mounts — re-claim focus a tick later so the caret is in the box.
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <textarea
      ref={inputRef}
      className={styles.textInput}
      // The input IS the box: anchored at its top-left, box-wide, and
      // previewing the active format — text wraps as the stamp will.
      style={{
        left: `${box.x}%`,
        top: `${box.y}%`,
        width: `${box.w}%`,
        fontSize: Math.min(40, Math.max(11, textStyle.size * contentWidthPx)),
        color: resolveMarkupColor(color),
        fontWeight: textStyle.bold ? 700 : 400,
        fontStyle: textStyle.italic ? 'italic' : 'normal',
        textDecoration: textStyle.underline ? 'underline' : 'none',
        textAlign: textStyle.align,
      }}
      dir={textStyle.direction}
      value={value}
      autoFocus
      rows={Math.max(1, value.split('\n').length)}
      aria-label={inputLabel}
      maxLength={500}
      onChange={(e) => onChange(e.target.value)}
      // Both stopped so the dialog's own key handling never closes the editor
      // under the input.
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) { e.preventDefault(); e.stopPropagation(); onCommit(); }
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
      }}
      onPointerDown={(e) => e.stopPropagation()}
    />
  );
}